<div
  class="chat-dock__resizer d-none d-lg-block bhashini-skip-translation"
  (mousedown)="onResizerMouseDown($event)"
  title="Drag to resize"
></div>

<!-- Dock panel -->
<div class="chat-dock__panel d-flex flex-column bg-white overflow-hidden bhashini-skip-translation">

  <!-- ── Header ──────────────────────────────────────────────────────────── -->
  <div class="d-flex align-items-center gap-2 px-2 py-3 header flex-shrink-0">
    <div class="d-flex align-items-center gap-2 flex-grow-1">
      <img
        src="images/ai-kosh-logo.svg"
        alt="AIKosh"
        width="64"
        height="36"
        class="flex-shrink-0"
      />
      <div>
        <div class="fw-600 text-sm">Ask Aikosh</div>
          <div class="d-flex align-items-center gap-1">
            <span
              class="d-inline-block rounded-circle flex-shrink-0 chat-dock__status-dot"
              [ngClass]="statusDotClass"
            ></span>
            <span class="text-xs text-gray">{{ statusLabel }}</span>
          </div>
      </div>
    </div>
    <div class="d-flex align-items-center flex-shrink-0 gap-2">
      <button
        class="button-no-style cursor-pointer p-1"
        (click)="close()"
        title="Minimize"
        aria-label="Close chat"
      >
        <img src="images/minimize-icon.svg" alt="Minimize" width="20" height="20" />
      </button>
       <button
        class="button-no-style cursor-pointer p-1"
        (click)="clearChat()"
        title="Close"
        aria-label="Clear conversation"
      >
        <img src="images/close.svg" alt="Close" width="16" height="16" />
      </button>
    </div>
  </div>

  <!-- ── Messages ───────────────────────────────────────────────────────── -->
  <div
    class="chat-dock__messages flex-grow-1 overflow-auto px-2 py-2"
    #messagesList
    (scroll)="onMessagesScroll($any($event.target))"
    (click)="onChatLinkClick($event)"
  >
    @let messages = chatState.messages$ | async;

    <!-- Empty state -->
    @if (messages?.length === 0 && !isLoading) {
      <div
        class="d-flex flex-column align-items-center justify-content-center h-100 text-center gap-3 py-5"
      >
        <img
          src="images/ai-kosh-logo.svg"
          alt="AIKosh"
          width="56"
          height="32"
          class="opacity-50"
        />
        <span class="d-block text-sm text-gray">
          Ask anything about the AIKosh platform, datasets, models, or policies.
        </span>
      </div>
    }

    @for (msg of messages ?? []; track trackById($index, msg)) {

      <!-- Date divider -->
      @if (isNewDay($index)) {
        <div class="chat-dock__date-divider position-relative d-flex align-items-center justify-content-center my-3">
          <span class="text-xs text-gray">{{ utilService.formatDateDivider(msg.timestamp) }}</span>
        </div>
      }

      <div
        class="d-flex mb-3"
        [class.justify-content-end]="msg.sender === 'user'"
        [class.justify-content-start]="msg.sender === 'bot'"
      >
        <!--
          Switch on sender — eliminates two sequential @if evaluations per row.
        -->
        @switch (msg.sender) {

          @case ('user') {
            <div class="chat-dock__bubble chat-dock__bubble--user text-sm">
              {{ msg.text }}
            </div>
          }

          @case ('bot') {
            <div class="d-flex flex-column gap-1 chat-dock__bot-msg-wrap">

              <!-- Bubble -->
              <div
                class="chat-dock__bubble chat-dock__bubble--bot text-sm"
                [class.tag-chip-error]="msg.isError"
              >
                <!--
                  Switch on content state — replaces two overlapping @if blocks.
                    'loading'   → animated dots (streaming, no text yet)
                    'streaming' → rendered text + blinking cursor
                    'done'      → rendered text only
                -->
                @switch (getMsgContentState(msg)) {
                  @case ('loading') {
                    <span class="d-flex gap-1 align-items-center">
                      <span class="chat-dock__dot"></span>
                      <span class="chat-dock__dot"></span>
                      <span class="chat-dock__dot"></span>
                    </span>
                  }
                  @case ('streaming') {
                    <div [innerHTML]="msg.text | safeMarkdown"></div>
                    <span class="chat-dock__cursor"></span>
                  }
                  @default {
                    <div [innerHTML]="msg.text | safeMarkdown"></div>
                  }
                }
              </div>

              <!--
                Action row — visible only after streaming completes and the
                back-end has confirmed the turn (turn_id !== undefined).
              -->
              @if (showActionRow(msg)) {
                <div class="d-flex align-items-center gap-2 ps-1">

                  <!--
                    Switch on feedback state — replaces two @if checks.
                      'thanks'  → confirmation label
                      'buttons' → thumbs-up / thumbs-down buttons
                      'none'    → no feedback UI (legacy / error message)
                  -->
                  @switch (getFeedbackState(msg)) {
                    @case ('thanks') {
                      <span class="text-xs text-gray chat-dock__feedback-thanks">
                        Thanks for your feedback!
                      </span>
                    }
                    @case ('buttons') {
                      <button
                        class="button-no-style cursor-pointer p-1 rounded-1 chat-dock__feedback-btn"
                        [class.chat-dock__feedback-btn--active]="msg.feedback === 'up'"
                        [disabled]="msg.feedbackPending"
                        title="Helpful"
                        aria-label="Mark as helpful"
                        (click)="onFeedbackClick(msg, 'up')"
                      >
                        <img
                          [src]="getThumbSrc(msg, 'up')"
                          alt="helpful"
                          width="16"
                          height="16"
                        />
                      </button>
                      <button
                        class="button-no-style cursor-pointer p-1 rounded-1 chat-dock__feedback-btn"
                        [class.chat-dock__feedback-btn--active]="msg.feedback === 'down'"
                        [disabled]="msg.feedbackPending"
                        title="Not helpful"
                        aria-label="Mark as not helpful"
                        (click)="onFeedbackClick(msg, 'down')"
                      >
                        <img
                          [src]="getThumbSrc(msg, 'down')"
                          alt="not helpful"
                          width="16"
                          height="16"
                          class="chat-dock__thumbs-down"
                        />
                      </button>
                    }
                  }

                  <button
                    class="button-no-style cursor-pointer p-1 rounded-1"
                    title="Copy"
                    aria-label="Copy message"
                    (click)="utilService.copyText('', msg.text)"
                  >
                    <img src="images/copy-black.svg" alt="copy" width="14" height="14" />
                  </button>
                  <!-- @if ($index === (messages?.length ?? 0) - 1) {
                    <button
                      class="button-no-style cursor-pointer p-1 rounded-1"
                      title="Retry"
                      aria-label="Retry this response"
                      (click)="retryFrom(msg)"
                    >
                      <img src="images/refresh-black.svg" alt="retry" width="16" height="16" />
                    </button>
                  } -->
                  <span class="text-xs text-gray ms-auto pe-1">{{ utilService.formatTime(msg.timestamp) }}</span>
                </div>

                <!-- Thumbs-down comment form -->
                @if (showCommentForm(msg)) {
                  <div class="chat-dock__comment-form mt-1 p-2 rounded-2 border">
                    <p class="text-xs text-gray mb-1">
                      What went wrong? <span class="text-muted">(optional)</span>
                    </p>
                    <textarea
                      class="form-control form-control-sm chat-dock__comment-textarea"
                      [formControl]="commentControl"
                      placeholder="Tell us more…"
                      rows="2"
                    ></textarea>
                    <div class="d-flex align-items-center justify-content-between mt-1 gap-2">
                      <span
                        class="text-xs"
                        [class.text-danger]="commentControl.invalid"
                        [class.text-muted]="!commentControl.invalid"
                      >{{ commentCharCount }}/{{ FEEDBACK_COMMENT_MAX }}</span>
                      <div class="d-flex gap-1">
                        <button
                          class="btn btn-link btn-sm p-0 text-muted text-decoration-none"
                          type="button"
                          [disabled]="msg.feedbackPending"
                          (click)="skipComment(msg)"
                        >
                          Skip
                        </button>
                        <button
                          class="btn btn-primary btn-sm py-0 px-2"
                          type="button"
                          [disabled]="commentControl.invalid || msg.feedbackPending"
                          (click)="submitComment(msg)"
                        >
                          Send
                        </button>
                      </div>
                    </div>
                  </div>
                }
              }

            </div>
          }

        }
      </div>
    }

    <!-- Scroll-to-bottom FAB -->
    @if (userScrolledUp) {
      <button
        class="button-no-style position-sticky border rounded-circle bg-white d-flex align-items-center justify-content-center chat-dock__scroll-btn"
        title="Scroll to latest"
        aria-label="Scroll to latest message"
        (click)="scrollToBottom()"
      >
        &darr;
      </button>
    }
  </div>

  <!-- ── Input row ───────────────────────────────────────────────────────── -->
  <div class="chat-dock__input-row px-3 pt-2 pb-2 flex-shrink-0 border-top">
    <div class="chat-dock__textarea-wrap position-relative">
      <app-textarea-input
        inputId="dock-message-input"
        label="noLabel"
        [control]="replyControl"
        placeholder="Ask a question..."
        [rows]="2"
        (keydown)="onEnterKey($any($event))"
      ></app-textarea-input>
      <button
        class="btn chat-dock__send-btn position-absolute d-flex align-items-center justify-content-center"
        [class.chat-dock__send-btn--stop]="isLoading"
        [disabled]="isSendDisabled"
        (click)="onSuffixAction()"
        type="button"
        [title]="isLoading ? 'Stop' : 'Send'"
        [attr.aria-label]="isLoading ? 'Stop generating' : 'Send message'"
      >
        @if (isLoading) {
          <span class="chat-dock__stop-icon"></span>
        } @else {
          <img [src]="sendIconSrc" alt="send" width="30" height="30" />
        }
      </button>
    </div>
  </div>

  <!-- ── Footer ─────────────────────────────────────────────────────────── -->
  <div class="chat-dock__footer d-flex align-items-center justify-content-between px-2 pb-2 pt-1">
    <span class="d-flex align-items-center gap-1 text-muted text-truncate" style="min-width: 0">
      <span class="text-truncate">Grounded in AIKosh content</span>
      <img
        src="images/info-black.svg"
        alt="info"
        width="12"
        height="12"
        class="flex-shrink-0 opacity-50"
        style="cursor: default"
        matTooltip="Responses may not cover all scenarios"
        matTooltipPosition="above"
      />
    </span>
    <span class="flex-shrink-0 ms-1" [class.text-danger]="replyControl.invalid">
      {{ inputCharCount }}/{{ MAX_INPUT_LENGTH }}
    </span>
  </div>

</div>
